import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const PRODUCTS = [
  {
    id: "rice",
    num: "01",
    name: "Rice",
    full: "Indian Basmati & Non-Basmati Rice",
    cat: "Grains & Cereals",
    hsn: "1006.30",
    img: "https://images.unsplash.com/photo-1536304993881-ff6e9eefa2a6?q=80&w=1200&auto=format&fit=crop",
    desc: "Aromatic long-grain Basmati 1121, Sella and IR64 non-Basmati rice, sorted with double-polished optical sorters.",
    specs: ["Moisture < 12%", "Purity 95%+", "Grain length 8.35 mm"],
  },
  {
    id: "onion",
    num: "02",
    name: "Onion",
    full: "Fresh Red & Nashik Onions",
    cat: "Fresh Vegetables",
    hsn: "0703.10",
    img: "https://images.unsplash.com/photo-1618512496248-a07fe83aa8cb?q=80&w=1200&auto=format&fit=crop",
    desc: "Graded export-quality red onions packed in reefer mesh bags with optimal humidity retention.",
    specs: ["Size 45–65 mm+", "45-day reefer shelf life", "Zero sprouting"],
  },
  {
    id: "foxnuts",
    num: "03",
    name: "Foxnuts",
    full: "Gourmet Foxnuts (Makhana)",
    cat: "Superfoods & Nuts",
    hsn: "1904.10",
    img: "https://images.unsplash.com/photo-1710421576768-ff985fa63b60?q=80&w=1200&auto=format&fit=crop",
    desc: "Hand-popped lotus seeds sourced directly from Bihar wetland growers, rich in protein and antioxidants.",
    specs: ["Grade 5–6 Suta", "Moisture < 8%", "Non-GMO"],
  },
  {
    id: "spices",
    num: "04",
    name: "Spices",
    full: "Authentic Whole & Ground Spices",
    cat: "Spices & Herbs",
    hsn: "0904.22",
    img: "https://images.unsplash.com/photo-1532336414038-cf19250c5757?q=80&w=1200&auto=format&fit=crop",
    desc: "High-curcumin turmeric, Guntur red chilli, green cardamom, cumin seeds and black pepper.",
    specs: ["Curcumin > 5%", "ASTA colour rated", "Steam sterilised"],
  },
  {
    id: "fruits",
    num: "05",
    name: "Fruits",
    full: "Tropical Indian Fruits",
    cat: "Fresh Produce",
    hsn: "0804.50",
    img: "https://images.unsplash.com/photo-1669207334420-66d0e3450283?q=80&w=1200&auto=format&fit=crop",
    desc: "Alphonso mangoes, pomegranates, fresh bananas and grapes harvested at peak ripeness.",
    specs: ["Cold-chain managed", "CA containerised", "Harvested at peak ripeness"],
  },
  {
    id: "eggs",
    num: "06",
    name: "Eggs",
    full: "Farm Fresh Table Eggs",
    cat: "Poultry",
    hsn: "0407.21",
    img: "https://images.unsplash.com/photo-1586802990181-a5771596eaea?q=80&w=1200&auto=format&fit=crop",
    desc: "White and brown table eggs, washed, UV-sterilised and packed in export paper pulp trays.",
    specs: ["Weight 50–60 g+", "Haugh units 72+", "Salmonella free"],
  },
  {
    id: "water",
    num: "07",
    name: "Water",
    full: "Himalayan Natural Mineral Water",
    cat: "Beverages",
    hsn: "2201.10",
    img: "https://images.unsplash.com/photo-1637905351378-67232a5f0c9b?q=80&w=1200&auto=format&fit=crop",
    desc: "Pure spring water bottled at the pristine Himalayan foothill aquifers near Dehradun.",
    specs: ["Natural pH 7.8–8.2", "Rich in electrolytes", "BPA-free packaging"],
  },
];

const Products = () => {
  const [active, setActive] = useState(PRODUCTS[0].id);
  const activeProduct = PRODUCTS.find((p) => p.id === active);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-forest grain py-20 sm:py-28 overflow-hidden"
    >
      <div
        className="absolute top-0 left-1/3 w-[30rem] h-[30rem] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #7DD3FC 0%, transparent 70%)" }}
      />
      <div className="relative z-10 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-sunset uppercase mb-5"
            >
              02 — Our Products
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl text-cream tracking-tight leading-[1.08]"
            >
              Seven export lines. <span className="italic text-gold">One standard.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-sm text-sm leading-relaxed text-cream/60"
          >
            Handpicked agricultural commodities sourced directly from trusted Indian
            farms and processed to international food-safety standards.
          </motion.p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* spotlight panel */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] rounded-t-full rounded-b-[2rem] overflow-hidden ring-1 ring-gold/30 shadow-2xl">
                <AnimatePresence mode="popLayout">
                  <motion.img
                    key={activeProduct.id}
                    data-testid="product-spotlight-image"
                    src={activeProduct.img}
                    alt={activeProduct.full}
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-8">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-sunset uppercase">
                    {activeProduct.cat} — HSN {activeProduct.hsn}
                  </p>
                  <p className="mt-2 font-display text-2xl text-cream leading-snug">
                    {activeProduct.full}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* index list */}
          <div className="lg:col-span-7">
            {PRODUCTS.map((p, i) => {
              const isActive = active === p.id;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.55, delay: i * 0.05 }}
                  className="border-b border-cream/12 first:border-t"
                >
                  <button
                    data-testid={`product-row-${p.id}`}
                    onMouseEnter={() => setActive(p.id)}
                    onClick={() => setActive(p.id)}
                    className={`w-full flex items-center gap-5 sm:gap-8 py-5 sm:py-6 text-left transition-colors duration-300 ${
                      isActive ? "text-sunset" : "text-cream/85 hover:text-cream"
                    }`}
                  >
                    <span className="font-mono text-xs text-cream/40">{p.num}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-display text-3xl sm:text-4xl lg:text-[2.6rem] leading-none tracking-tight">
                        {p.name}
                      </span>
                      <span className="block sm:hidden mt-1 font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                        {p.cat} — HSN {p.hsn}
                      </span>
                    </span>
                    <span className="hidden sm:block font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                      {p.cat}
                    </span>
                    <span className="hidden sm:block font-mono text-xs text-gold/80">
                      {p.hsn}
                    </span>
                    <ArrowUpRight
                      className={`w-5 h-5 shrink-0 transition-all duration-300 ${
                        isActive ? "text-sunset rotate-0" : "text-cream/30 rotate-45"
                      }`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-7 pl-9 sm:pl-14 pr-2">
                          <img
                            src={p.img}
                            alt={p.full}
                            className="sm:hidden w-full aspect-[16/10] object-cover rounded-xl mb-4"
                          />
                          <p className="text-sm leading-relaxed text-cream/65 max-w-lg">
                            {p.desc}
                          </p>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {p.specs.map((s) => (
                              <span
                                key={s}
                                className="rounded-full border border-gold/30 px-3 py-1.5 font-mono text-[10px] tracking-[0.12em] text-gold/90 uppercase"
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                data-testid="products-quote-cta"
                onClick={() => scrollToSection("#contact")}
                className="group rounded-full bg-sunset px-8 py-4 font-grotesk text-sm font-semibold uppercase tracking-wider text-forest hover:bg-sunset-bright transition-colors duration-300"
              >
                Request Export Quote
                <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform duration-300">
                  →
                </span>
              </button>
              <a
                data-testid="products-catalogue-link"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/catalogue`}
                className="inline-flex items-center gap-3 rounded-full border border-gold/40 px-8 py-4 font-grotesk text-sm font-medium uppercase tracking-wider text-gold hover:bg-gold/10 transition-colors duration-300"
              >
                <span className="font-mono">↓</span>
                Download Catalogue (PDF)
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
